import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';

import {
  clearFee,
  setFeePercentage,
  setMaxWorkingDays
} from '@epi-actions/api';
import { employeesPerPage } from '@epi-constants/topupEmployees';
import { postTopupEmployeesRequest } from '@epi-api/topupLoad';
import { BenefitType, ON_HOLD, Schedule } from '@epi-constants/benefitTypes';
import { getCompanyMetadata } from '@epi-store/companyMetadata/companyMetadata.selectors';
import { topupSliceActions } from '@epi-store/topup/topup.slice';
import { FormFields } from '@epi-forms/helpers';
import { formatDate } from '@epi-pages/Reports/utils';
import { isTomorrow } from '@epi-helpers/TopupHelper';
import { Employee } from '@epi-models/pages/Transfer';
import { formatCurrencyDecimal, getNumber } from '@epi-helpers/numeral';
import {
  getFeePercentage,
  getFeesVatPercentage,
  getMaxWorkingDays
} from '@epi-selectors/settings';
import { getMainAvailableBalance } from '@epi-selectors/companyAccounts';
import { TopupFormType } from '@epi-pages/LoadBenefit/components/Topup/TopupForm';
import { useGetBenefitGroupsQuery } from '@epi-api/benefitGroups';

export const useTopupEmployees = () => {
  const dispatch = useDispatch();
  const { watch, setValue } = useFormContext<TopupFormType>();
  const [employeesToTopup, setEmployeesToTopup] = useState<Employee[]>([]);
  const [forceUpdate, setForceUpdate] = useState(false);
  const companyMetadata = useSelector(getCompanyMetadata);
  const maxWorkingDays = useSelector(getMaxWorkingDays);
  const { data } = useGetBenefitGroupsQuery();

  const benefitType = watch(FormFields.Benefit);
  const scheduleType = watch(FormFields.ScheduleType);
  const scheduleDate = watch(FormFields.ScheduleDate);
  const checkableEmployees = watch(FormFields.CheckableEmployees);
  const daysToTopup = watch(FormFields.DaysForLunari) ?? 0;
  const benefitGroupIds = watch(FormFields.BenefitGroupIds);

  const lunchSettingsInGroup = data?.length
    ? data[0].benefitSettings.find(
        ({ benefitType }) => benefitType === BenefitType.Lunch
      )
    : undefined;

  const lunchLoadValue = lunchSettingsInGroup
    ? lunchSettingsInGroup.maxAmountPerPeriod
    : 0;

  const daysExceed = employeesToTopup.some(
    employee =>
      employee?.remainingDays &&
      employee.remainingDays < daysToTopup &&
      daysToTopup <= maxWorkingDays
  );

  const checkedCount = employeesToTopup.length;

  const lowestYearlyLimit = Math.min(
    ...employeesToTopup
      .filter(employee => employee.availableToTopUpAmount > 0)
      .map(employee => employee.availableToTopUpAmount)
  );

  const topupBeneficiariesIds = employeesToTopup.map(
    employee => employee.beneficiaryId
  );

  const isAllEmployeeChecked = !checkableEmployees.find(
    emp => !emp.isChecked && !emp.disabled
  );

  const toggleEmployee = employee => {
    setValue(
      FormFields.CheckableEmployees,
      checkableEmployees.map(emp =>
        emp.beneficiaryId === employee.beneficiaryId
          ? { ...employee, isChecked: !employee.isChecked }
          : emp
      )
    );
  };

  const toggleAllEmployee = isChecked => {
    setValue(
      FormFields.CheckableEmployees,
      checkableEmployees.map(emp => ({
        ...emp,
        isChecked:
          !emp.disabled && emp.availableToTopUpAmount > 0 ? isChecked : false
      }))
    );
  };

  const loadEmployeesToTopup = async () => {
    const response = await postTopupEmployeesRequest(
      formatDate(scheduleDate),
      benefitType,
      benefitGroupIds
    );
    const togglerFromBE = companyMetadata.data?.showOnHoldBeneficiaries;

    dispatch(
      setFeePercentage(response.availableBenefitSettings[0]?.feePercentage)
    );
    dispatch(
      setMaxWorkingDays(response.availableBenefitSettings[0]?.maxWorkingDays)
    );
    setValue(
      FormFields.CheckableEmployees,
      response.beneficiaries.map(employee => {
        const isOnHold = employee.employmentType === ON_HOLD;
        const disabled =
          employee.availableToTopUpAmount <= 0 ||
          (benefitType === BenefitType.Lunch && employee.remainingDays === 0) ||
          ((benefitType === BenefitType.Transport ||
            benefitType === BenefitType.Lunch) &&
            isOnHold);
        const isCheckedOnHold = isOnHold ? togglerFromBE : !disabled;

        return {
          ...employee,
          availableToTopUpAmount: employee.availableToTopUpAmount / 100,
          isChecked: disabled ? false : isCheckedOnHold,
          onHold: isOnHold,
          disabled,
          loadValue: lunchLoadValue,
          fundsLoadingType: employee.fundsLoadingType
        };
      })
    );
  };

  const paginationCount = Math.ceil(
    checkableEmployees.length / employeesPerPage
  );

  useEffect(() => {
    setEmployeesToTopup(
      checkableEmployees.filter(employee => employee.isChecked)
    );
  }, [checkableEmployees]);

  useEffect(() => {
    return () => {
      dispatch(clearFee());
    };
  }, []);

  useEffect(() => {
    if (benefitType && scheduleType) {
      if (scheduleType === Schedule.Direct) {
        setForceUpdate(false);
        if (!scheduleDate) {
          loadEmployeesToTopup();
        }
      } else {
        if (scheduleDate) {
          setForceUpdate(false);
          if (isTomorrow(scheduleDate)) {
            loadEmployeesToTopup();
          }
        }
      }
    } else {
      setValue(FormFields.CheckableEmployees, []);
      dispatch(topupSliceActions.toggleShowTable(false));
    }
  }, [benefitType, forceUpdate, scheduleType, scheduleDate, benefitGroupIds]);

  const balance = useSelector(getMainAvailableBalance);
  const vat = useSelector(getFeesVatPercentage);
  const fee = useSelector(getFeePercentage);
  const topupPerPerson = watch(FormFields.MoneyAmount);
  const numeralTopupPerPerson = getNumber(topupPerPerson);

  const calculateTotalAmountOfRateByDays = (
    employees: Employee[],
    ...rates: number[]
  ) =>
    employees.reduce((acc, employee): number => {
      const topupValue =
        employee.remainingDays && employee.remainingDays < daysToTopup
          ? employee.remainingDays * (employee.loadValue ?? 0)
          : daysToTopup * (employee.loadValue ?? 0);

      const valueRateAmount = rates.reduce((rateValue, rate) => {
        return getNumber(((rateValue / 100) * rate).toFixed(2));
      }, topupValue);

      return acc + valueRateAmount;
    }, 0);

  const topupFromDays = () =>
    calculateTotalAmountOfRateByDays(employeesToTopup);

  const topupFromAmount = () =>
    topupPerPerson
      ? getNumber((numeralTopupPerPerson * checkedCount).toFixed(2))
      : 0;

  const feePerEmployee = Math.round(numeralTopupPerPerson * fee) / 100;
  const feeTotal = getNumber((feePerEmployee * checkedCount).toFixed(2));

  const feeTotalFromDays = calculateTotalAmountOfRateByDays(
    employeesToTopup,
    fee
  );
  const vatPerEmployee = Math.round(feePerEmployee * vat) / 100;
  const vatTotal = vatPerEmployee * checkedCount;

  const vatTotalFromDays = calculateTotalAmountOfRateByDays(
    employeesToTopup,
    fee,
    vat
  );

  const topupTotal =
    benefitType === BenefitType.Lunch ? topupFromDays() : topupFromAmount();
  const sumTotal = getNumber(
    benefitType === BenefitType.Lunch
      ? topupFromDays() + feeTotalFromDays + vatTotalFromDays
      : (numeralTopupPerPerson * checkedCount + feeTotal + vatTotal).toFixed(2)
  );
  const remainingBalance = balance - sumTotal;

  const formattedTopup = formatCurrencyDecimal(topupTotal);
  const formattedFee = formatCurrencyDecimal(
    benefitType === BenefitType.Lunch ? feeTotalFromDays : feeTotal
  );
  const formattedVat = formatCurrencyDecimal(
    benefitType === BenefitType.Lunch ? vatTotalFromDays : vatTotal
  );
  const formattedTopupWithFees = formatCurrencyDecimal(sumTotal);
  const formattedRemainingBalance = formatCurrencyDecimal(remainingBalance);
  const formattedMissingAmount = formatCurrencyDecimal(
    Math.abs(remainingBalance)
  );

  return {
    paginationCount,
    toggleEmployee,
    checkedCount,
    lowestYearlyLimit,
    topupBeneficiariesIds,
    toggleAllEmployee,
    isAllEmployeeChecked,
    setForceUpdate,
    formattedTopup,
    formattedFee,
    formattedVat,
    formattedTopupWithFees,
    formattedRemainingBalance,
    remainingBalance,
    fee,
    vat,
    formattedMissingAmount,
    employeesToTopup,
    daysExceed,
    checkableEmployees
  };
};
